import { boardingWorkPoint } from "../domain/queues.js";
import { System, SystemImpl } from "minecs";
import type { GameModel } from "yage/game/GameModel";
import { Transform } from "yage/schemas/entity/Transform";
import { AirportState, AirportBag, AirportAgent, AirportStaff, AirportCash, AirportTrash } from "../components/index.js";
import { CONFIG, stationPad, LOADING_PAD, RACK_PICKUP, onPad, BINS, binPad, cashPoint, VENDING, ADMIN } from "../domain/config.js";
import { addMoney } from "../domain/economy.js";
import { state, career, ids, stations, distance, route, feedback, vending } from "../services/world.js";
import { staffCarry } from "../domain/staff.js";
import { shiftsActive, STAFF_ROOM } from "../domain/act2.js";
import { crewTask, worksDesk, boardingCrewGate } from '../services/crew.js';
import { tier } from "../domain/airports.js";
@System(AirportStaff)
export class AirportStaffRouting extends SystemImpl<GameModel> {
    static depth = 1500;
    run = (g: GameModel, id: number) => {
        if (!state(g).loaded)
            return;
        const s = g.getTypedUnsafe(AirportStaff, id);
        if (s.role === 'pilot') return;
        // MGT-003/004: training happens at the admin desk and breaks in the staff room; neither counts as desk work.
        if (s.training > 0 || s.onBreak) { s.stage = -1; route(g, id, s.training > 0 ? ADMIN : STAFF_ROOM); return; }
        if (s.role === 'clerk' && s.stage < 0 && s.homeStage >= 0) s.stage = s.homeStage;
        if (s.role === 'cleaner') return routeCleaner(g, id, s);
        if (s.role === 'runner') return routeRunner(g, id);
        if (s.role === 'crew') s.stage = crewTask(g, id);
        if (worksDesk(s)) {
            const gate = s.stage === 6 ? boardingCrewGate(g, id) : -1;
            route(g, id, gate >= 0 ? boardingWorkPoint(gate, tier(state(g)).code) : stationPad(s.stage, 'work'));
            return;
        }
        const bags = ids(g, AirportBag), carried = bags.filter(b => g.getTypedUnsafe(AirportBag, b).carrier === id);
        if (carried.length >= staffCarry(s.recordId, 'bags') || s.task === 1 && carried.length) {
            s.task = 1;
            route(g, id, LOADING_PAD);
            return;
        }
        const free = bags.filter(b => g.getTypedUnsafe(AirportBag, b).carrier < 0).sort((a, b) => g.getTypedUnsafe(AirportBag, a).serial - g.getTypedUnsafe(AirportBag, b).serial);
        s.task = 0;
        if (free.length)
            route(g, id, g.getTypedUnsafe(Transform, free[0]));
        else if (carried.length) {
            s.task = 1;
            route(g, id, LOADING_PAD);
        }
        else
            route(g, id, RACK_PICKUP);
    };
}
@System(AirportState)
export class AirportBaggage extends SystemImpl<GameModel> {
    static depth = 4600;
    run = (g: GameModel) => {
        const e = state(g);
        if (!e.loaded)
            return;
        for (const id of [...ids(g, AirportAgent), ...ids(g, AirportStaff).filter(a => { const staff = g.getTypedUnsafe(AirportStaff, a); return staff.role === 'porter' || staff.role === 'crew' && staff.stage < 0; })].sort((a, b) => a - b)) {
            const p = g.getTypedUnsafe(Transform, id), isAgent = g.hasComponent(AirportAgent, id), max = isAgent ? 4 + e.bagLevel * 2 : staffCarry(g.getTypedUnsafe(AirportStaff, id).recordId, 'bags');
            let count = ids(g, AirportBag).filter(b => g.getTypedUnsafe(AirportBag, b).carrier === id).length;
            if (onPad('belt', p, LOADING_PAD) && count) {
                for (const b of ids(g, AirportBag))
                    if (g.getTypedUnsafe(AirportBag, b).carrier === id)
                        g.removeEntity(b);
                e.bagsDelivered += count;
                if (shiftsActive(e)) e.holdBags += count; // MGT-007: delivered bags wait in the hold for departing aircraft.
                if (isAgent)
                    feedback(g, id, `Loaded ${count} bags. Ready for boarding.`);
                count = 0;
            }
            for (const b of ids(g, AirportBag)) {
                const bag = g.getTypedUnsafe(AirportBag, b);
                if (bag.carrier < 0 && count < max && distance(p, g.getTypedUnsafe(Transform, b)) < 48) {
                    bag.carrier = id;
                    count++;
                }
            }
        }
        for (const id of ids(g, AirportBag)) {
            const b = g.getTypedUnsafe(AirportBag, id);
            if (b.carrier < 0)
                continue;
            if (!g.isActive(b.carrier)) {
                b.carrier = -1;
                continue;
            }
            const p = g.getTypedUnsafe(Transform, b.carrier);
            const t = g.getTypedUnsafe(Transform, id);
            t.x = p.x;
            t.y = p.y;
        }
    };
}
@System(AirportState)
export class AirportCollections extends SystemImpl<GameModel> {
    static depth = 4650;
    run = (g: GameModel) => {
        const e = state(g);
        if (!e.loaded)
            return;
        const ss = stations(g);
        for (const cash of ids(g, AirportCash)) {
            const c = g.getTypedUnsafe(AirportCash, cash), s = c.vending >= 0 ? vending(g)[c.vending] : ss[c.stage];
            if (!s || s.pending <= 0)
                continue;
            for (const id of ids(g, AirportAgent)) {
                if (distance(g.getTypedUnsafe(Transform, id), g.getTypedUnsafe(Transform, cash)) > 55)
                    continue;
                const amount = addMoney(career(g), e, s.pending, false);
                s.pending -= amount;
                e.collected += amount;
                feedback(g, id, `Collected ${amount} bills from ${c.vending >= 0 ? 'the vending machine' : CONFIG.stages[c.stage].name}.`);
                e.revision++;
                break;
            }
        }
    };
}
/** Cleaners collect the nearest loose litter, then empty a full (or final) stack at the nearest open bin. */
function routeCleaner(g: GameModel, id: number, s: AirportStaff) {
    const at = g.getTypedUnsafe(Transform, id), litter = ids(g, AirportTrash);
    const carried = litter.filter(t => g.getTypedUnsafe(AirportTrash, t).carrier === id).length;
    const free = litter.filter(t => g.getTypedUnsafe(AirportTrash, t).carrier < 0)
        .sort((a, b) => distance(at, g.getTypedUnsafe(Transform, a)) - distance(at, g.getTypedUnsafe(Transform, b)) || a - b);
    if (carried >= staffCarry(s.recordId, 'litter') || carried && !free.length) {
        s.task = 1;
        const bin = BINS.map(binPad).filter(b => b.x < tier(state(g)).maxX).sort((a, b) => distance(at, a) - distance(at, b))[0];
        if (bin) route(g, id, bin);
        return;
    }
    s.task = 0;
    if (free.length) route(g, id, g.getTypedUnsafe(Transform, free[0]));
}
/**
 * Cash runners (Act II only) carry floor cash to the wallet: they walk to the fullest cash stack (at least 40 bills)
 * and bank it on arrival. Before the Promotion only captains collect cash.
 */
export const RUNNER_MIN = 40;
function routeRunner(g: GameModel, id: number) {
    const e = state(g);
    if (!shiftsActive(e)) return;
    const at = g.getTypedUnsafe(Transform, id);
    const stacks = [...stations(g).filter(s => s.unlocked && s.pending >= RUNNER_MIN).map(s => ({ owner: s as { pending: number }, point: cashPoint(s.index) })),
        ...vending(g).filter(v => v.pending >= RUNNER_MIN).map(v => ({ owner: v as { pending: number }, point: { x: VENDING[v.index].x + 56, y: VENDING[v.index].y + 64 } }))]
        .sort((a, b) => b.owner.pending - a.owner.pending || distance(at, a.point) - distance(at, b.point));
    const target = stacks[0];
    if (!target) return;
    if (distance(at, target.point) < 30) { addMoney(career(g), e, target.owner.pending, false); target.owner.pending = 0; e.revision++; return; }
    route(g, id, target.point);
}
